import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { RouterModule } from "@angular/router";

import { Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { SessionService } from "../_services";
import { WebAuthnService } from "../_services/webauthn.service";

@Component({
  imports: [ReactiveFormsModule, RouterModule],
  selector: "hawk-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  host: {
    class: "app-page",
  },
})
export class LoginComponent implements OnInit {
  private sessionService: SessionService = inject(SessionService);
  private fb = inject(FormBuilder);
  private webAuthn = inject(WebAuthnService);
  private destroyed = false;
  // Zählt die Autofill-Starts. Jeder andere Login (Button, Passwort) und das
  // Verlassen der Seite erhöhen sie, damit ein noch ladender Autofill danach
  // keinen Browser-Dialog mehr öffnet.
  private autofillGeneration = 0;

  protected form = this.fb.group({
    email: this.fb.control({ value: "", disabled: false }, [Validators.required]),
    password: this.fb.control({ value: "", disabled: false }, [Validators.required]),
  });
  protected error = signal<string>("");
  protected returnUrl = inject(ActivatedRoute).snapshot.queryParams.returnUrl || "/";

  // Passkeys zusätzlich zum Passwort (siehe bentomax/hawk#596)
  protected passkeySupported = this.webAuthn.supportsWebAuthn();
  protected passkeyBusy = signal(false);

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      this.autofillGeneration++;
      // Offenen Autofill-Aufruf nicht auf der nächsten Seite weiterlaufen lassen
      this.webAuthn.cancel();
    });
  }

  public ngOnInit() {
    this.startPasskeyAutofill();
  }

  public async onSubmit() {
    // Wartender Autofill-Aufruf stört den Passwort-Login nicht, wird aber
    // sauber beendet und bei einem Fehlschlag neu gestartet.
    this.autofillGeneration++;
    this.webAuthn.cancel();

    const form = this.form.value;

    await this.sessionService.loginRedirect(form.email, form.password, this.returnUrl);
    if (this.sessionService.loginError) {
      this.error.set(this.sessionService.loginError);
      this.startPasskeyAutofill();
    } else {
      this.error.set("");
    }
  }

  public async onPasskeyLogin() {
    this.error.set("");
    this.passkeyBusy.set(true);

    // Ein Autofill, der noch seine Options lädt, öffnet danach keinen Dialog
    // mehr. Einen bereits wartenden bricht startAuthentication selbst ab (über
    // den AbortController von simplewebauthn), er meldet sich still als
    // „cancelled“. Neu gestartet wird er erst, wenn der Button-Login vorbei ist.
    this.autofillGeneration++;
    const result = await this.sessionService.loginWithPasskey(this.returnUrl);

    this.passkeyBusy.set(false);
    if (result.status === "failed") {
      this.error.set(result.message);
    }
    if (result.status !== "success") {
      this.startPasskeyAutofill();
    }
  }

  // Conditional UI: Der Browser schlägt vorhandene Passkeys direkt im
  // E-Mail-Feld vor, bei Auswahl wird sofort angemeldet.
  private async startPasskeyAutofill() {
    const generation = ++this.autofillGeneration;
    const stillWanted = () => generation === this.autofillGeneration;

    if (!this.passkeySupported || this.destroyed || !(await this.webAuthn.supportsAutofill()) || !stillWanted()) {
      return;
    }

    const result = await this.sessionService.loginWithPasskey(this.returnUrl, true, stillWanted);
    if (result.status === "failed" && !this.destroyed) {
      this.error.set(result.message);
    }
  }
}
